import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavFollows } from '@/lib/follows';
import type { ChannelAccessUnits } from '@tradeoutlet/shared';
import { ChannelAccessPicker } from './ChannelAccess';
import { PersonButton } from '@/features/people/PersonDrawer';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ListChecks, Lock, UserMinus, UserPlus } from 'lucide-react';
import type { Conversation } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { messageTimestamp } from '@/lib/format';
import { useChatSections, useMembers, useWorkspaceId } from '@/lib/hooks';
import { buildingCreateFields, canChooseSection, channelPrefill, copiedChannelCreateFields, initialBuildingChoice, initialSectionChoice, peopleCountText, sectionCreateFields, type ChannelPrefill, type ChannelRules, type ChannelTemplate } from '@tradeoutlet/shared';
import { ChannelPermissionsSection } from './ChannelPermissions';
import { SectionField } from './SectionPicker';
import { BuildingField } from './BuildingField';
import { useBuildings } from '@/features/buildings/common';
import { qk, queryClient } from '@/lib/queryClient';
import { usePermissionsAt } from '@/lib/permissions';
import { useUi } from '@/lib/store';
import { Avatar, Button, EmptyState, ErrorBox, Field, Input, Modal, Spinner, Textarea, Toggle } from '@/components/ui';
import { confirmRemoval } from '@/lib/confirmRemove';
import { MessageBody } from './MessageBody';
import { PeoplePicker } from '@/features/people/PeoplePicker';
import { StaffListPicker } from '@/features/people/StaffListPicker';
import { useChatLimits } from '@/features/settings/PrefsPanels';
import { BottomSheet } from '@/mobile/BottomSheet';
import { useMobileLayout } from '@/mobile/layout';

export { PeoplePicker };

/** t446u: `sectionId` preselects a section (a section's + in the sidebar or the phone Channels list). */
export function NewChannelModal({ open, onClose, sectionId: presetSection = null, buildingId: presetBuilding = null, fromChannelId = null }: { open: boolean; onClose: () => void; sectionId?: string | null; /** t446u: opened from a building's page: start on that building. */ buildingId?: string | null;
  /** t557u: "New channel with these members…": start from this channel's people, roles and settings (name and description stay empty). */ fromChannelId?: string | null }) {
  const ws = useWorkspaceId();
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setPrivate] = useState(true);
  const [members, setMembers] = useState<string[]>([]);
  const [units, setUnits] = useState<ChannelAccessUnits>({ team_ids: [], location_ids: [] });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const channelLimit = useChatLimits()?.channel_member_limit;
  const sectionsQ = useChatSections();
  const sections = sectionsQ.data?.sections ?? [];
  const showSection = canChooseSection(sectionsQ.data?.can_manage);
  const [sectionId, setSectionId] = useState<string | null>(presetSection);
  const buildingsQ = useBuildings();
  const [buildingId, setBuildingId] = useState<string | null>(presetBuilding);
  useEffect(() => { if (open) { setName(''); setDescription(''); setPrivate(true); setMembers([]); setUnits({ team_ids: [], location_ids: [] }); setError(null); setSectionId(presetSection); setBuildingId(presetBuilding); setPrefill(null); setRules(null); setTemplate({}); } }, [open, presetSection, presetBuilding, fromChannelId]);
  // t557u: copying a channel's membership. Messages, pins, files, integrations and any form aren't copied.
  const [template, setTemplate] = useState<{ data?: ChannelTemplate; error?: unknown }>({});
  const [prefill, setPrefill] = useState<ChannelPrefill | null>(null);
  const [rules, setRules] = useState<ChannelRules | null>(null);
  const onTemplate = useCallback((data: ChannelTemplate | undefined, err: unknown, p: ChannelPrefill | null) => {
    setTemplate({ data, error: err });
    if (!p) return;
    setPrefill(p); setPrivate(p.isPrivate); setMembers(p.memberIds); setUnits(p.access); setSectionId(p.sectionId); setBuildingId(p.buildingId);
  }, []);
  const copying = Boolean(fromChannelId);
  const managers = prefill ? prefill.managerIds.filter(id => members.includes(id)).length : 0;
  const submit = async () => {
    if (!ws) return;
    setBusy(true);
    try {
      const c = await api.createChannel(ws, { name, description: description || undefined, is_private: isPrivate, member_ids: members, access_team_ids: units.team_ids, access_location_ids: units.location_ids,
        ...(copying && prefill ? copiedChannelCreateFields(prefill, members, rules ?? prefill.permissions) : {}),
        ...sectionCreateFields(showSection ? initialSectionChoice(sections, sectionId) : null), ...buildingCreateFields(initialBuildingChoice(buildingsQ.data?.buildings, buildingId)) });
      await queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
      onClose();
      nav(`/c/${c.id}`);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  // t438u: on a phone Create a channel is a bottom sheet (swipe down or the backdrop closes it, no ✕), like the app's.
  const phone = useMobileLayout();
  const create = <Button className={phone ? 'w-full' : undefined} onClick={() => void submit()} loading={busy} disabled={!name.trim() || (copying && !prefill)} data-testid="new-channel-create">Create</Button>;
  const Frame = phone ? BottomSheet : Modal;
  return (
    <Frame open={open} onClose={onClose} title="Create a channel" {...(phone ? { testId: 'new-channel-sheet' } : {})}
      footer={phone ? create : <><Button variant="ghost" onClick={onClose}>Cancel</Button>{create}</>}>
      <div className={phone ? 'space-y-4 px-4 pb-2' : 'space-y-4'}>
        <Field label="Name" hint="Lowercase, no spaces - e.g. warehouse-ops"><Input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="e.g. warehouse-ops" /></Field>
        <Field label="Description"><Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} /></Field>
        {showSection && <SectionField sections={sections} value={initialSectionChoice(sections, sectionId)} onChange={setSectionId} phone={phone} />}
        <BuildingField value={initialBuildingChoice(buildingsQ.data?.buildings, buildingId)} onChange={setBuildingId} phone={phone} />
        {/* t442u: private by default; untick it for a channel anyone in the workspace can find and join. */}
        <div data-testid="new-channel-private">
          <Toggle checked={isPrivate} onChange={setPrivate} label={<span className="flex items-center gap-1"><Lock className="h-3.5 w-3.5" />Private</span>} />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-testid="new-channel-private-hint">{isPrivate ? 'Only people you add can see it' : 'Anyone in the workspace can find and join it'}</p>
        </div>
        {open && fromChannelId && !prefill && <ChannelTemplateLoader channelId={fromChannelId} onLoaded={onTemplate} />}
        {copying && !prefill && !template.error && <div className="flex justify-center py-2"><Spinner /></div>}
        {copying ? (prefill && (
          <Field label="People" hint={`${peopleCountText(members.length + 1)} including you${managers ? `, ${managers === 1 ? '1 manager stays a manager' : `${managers} managers stay managers`}` : ''}`}>
            <div data-testid="new-channel-copied-people"><PeoplePicker selected={members} onChange={setMembers} max={channelLimit ? channelLimit - 1 : undefined} chipsUpTo={12} /></div>
          </Field>
        )) : (
          <Field label="Add people (optional)" hint={channelLimit ? `Channels hold up to ${channelLimit} people, including you. You can add more from the staff list once it's created.` : undefined}><PeoplePicker selected={members} onChange={setMembers} max={channelLimit ? channelLimit - 1 : undefined} /></Field>
        )}
        {(!copying || prefill) && <Field label="Access (optional)" hint={isPrivate ? 'People in these can read it without being added' : 'People in these are listed and can join'}><ChannelAccessPicker value={units} onChange={setUnits} /></Field>}
        {copying && prefill && fromChannelId && <ChannelPermissionsSection convId={fromChannelId} saved={prefill.permissions} draft={rules} onDraft={setRules} disabled={busy} />}
        {copying && template.data && (
          <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="new-channel-copy-note">
            Settings from #{template.data.source_slug} come too. Messages, pins, files and integrations don’t.{template.data.had_form ? ' Its form doesn’t either, so this is a normal channel.' : ''}
          </p>
        )}
        <ErrorBox error={template.error ? errorMessage(template.error) : null} />
        <ErrorBox error={error} />
      </div>
    </Frame>
  );
}

/** t557u: loads the source channel for "New channel with these members…" (kept out of the plain form so it needs no query client). */
function ChannelTemplateLoader({ channelId, onLoaded }: { channelId: string; onLoaded: (t: ChannelTemplate | undefined, error: unknown, p: ChannelPrefill | null) => void }) {
  const { userId } = useAuth();
  const q = useQuery({ queryKey: ['channel-template', channelId], queryFn: () => api.channelTemplate(channelId), staleTime: 0, gcTime: 0, retry: false });
  useEffect(() => {
    if (q.data) onLoaded(q.data, null, channelPrefill(q.data, userId));
    else if (q.error) onLoaded(undefined, q.error, null);
  }, [q.data, q.error, userId, onLoaded]);
  return null;
}

export function NewDmModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const nav = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const groupMax = (useChatLimits()?.group_dm_limit ?? 50) - 1;
  useEffect(() => { if (open) { setSelected([]); setError(null); } }, [open]);
  // t351u: people you follow first.
  const follows = useNavFollows();
  const following = useMemo(() => (follows.data?.people ?? []).map(p => p.user_id), [follows.data]);
  const submit = async () => {
    if (!ws) return;
    setBusy(true);
    try {
      const c = await api.openDm(ws, selected);
      await queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
      onClose();
      nav(`/c/${c.id}`);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="New message"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={() => void submit()} loading={busy} disabled={!selected.length}>{selected.length > 1 ? 'Start group chat' : 'Open chat'}</Button></>}>
      <p className="mb-3 text-sm text-slate-500">Pick one person for a direct message, or several (up to {groupMax}) for a group chat. For more people, use a channel.</p>
      <PeoplePicker selected={selected} onChange={setSelected} exclude={userId ? [userId] : []} max={groupMax} following={following} />
      <div className="mt-3"><ErrorBox error={error} /></div>
    </Modal>
  );
}

export function PinsModal({ open, onClose, conversationId }: { open: boolean; onClose: () => void; conversationId: string }) {
  const pins = useQuery({ queryKey: qk.pins(conversationId), queryFn: () => api.pins(conversationId), enabled: open });
  const { byId } = useMembers();
  return (
    <Modal open={open} onClose={onClose} title="Pinned messages" wide>
      {pins.isLoading && <div className="flex justify-center p-6"><Spinner /></div>}
      {pins.data?.messages.length === 0 && <EmptyState title="Nothing pinned yet">Use the ⋯ menu on a message to pin it here.</EmptyState>}
      <div className="space-y-3">
        {pins.data?.messages.map(m => (
          <a key={m.id} href={`#m-${m.id}`} onClick={onClose} className="block rounded-lg border border-slate-200 p-3 hover:border-accent-line dark:border-slate-700">
            <div className="mb-1 flex items-center gap-2 text-sm"><Avatar size={20} name={byId.get(m.sender_id)?.display_name} id={m.sender_id} /><span className="font-semibold">{byId.get(m.sender_id)?.display_name}</span><span className="text-xs text-slate-500">{messageTimestamp(m.created_at)}</span></div>
            <MessageBody body={m.body} />
          </a>
        ))}
      </div>
    </Modal>
  );
}

/** Members window for issue and event discussions (entity threads); channels, rooms and chats use MembersPanel (t245u). */
export function MembersModal({ open, onClose, conversation }: { open: boolean; onClose: () => void; conversation: Conversation }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const presence = useUi(s => s.presence);
  const toast = useUi(s => s.toast);
  const [adding, setAdding] = useState<string[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [staffList, setStaffList] = useState(false);
  const [busy, setBusy] = useState(false);
  const members = conversation.members ?? [];
  const limits = useChatLimits();
  const isRoom = conversation.kind === 'room';
  const limit = isRoom ? limits?.room_member_limit : limits?.channel_member_limit;
  const isChannel = conversation.kind === 'channel' || isRoom;
  const left = isChannel && limit ? Math.max(0, limit - members.length) : undefined;
  const here = usePermissionsAt(`conversation:${conversation.id}`).data;
  const canRemove = isRoom ? Boolean(conversation.can_manage) && !conversation.archived_at : Boolean(here?.has('chat.channel.manage'));
  const canAdd = isRoom ? Boolean(conversation.can_add_members) : conversation.is_member;
  const where = isRoom ? `“${conversation.title ?? 'the ad-hoc meeting room'}”` : `#${conversation.slug ?? 'channel'}`;
  // On a phone the Members window is a bottom sheet, like the other phone windows.
  const phone = useMobileLayout();
  const Frame = phone ? BottomSheet : Modal;
  const refresh = () => { void queryClient.invalidateQueries({ queryKey: qk.conversation(conversation.id) }); void queryClient.invalidateQueries({ queryKey: qk.conversations(ws ?? '') }); };
  const add = async (ids = adding) => {
    setBusy(true);
    try {
      const r = await api.addConversationMembers(conversation.id, ids);
      toast(`Added ${r.added} ${r.added === 1 ? 'person' : 'people'}`, 'success');
      setAdding([]); setShowAdd(false); setStaffList(false); refresh();
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  const remove = async (uid: string) => {
    if (!await confirmRemoval([members.find(m => m.user_id === uid)?.display_name ?? 'this person'], isRoom ? 'this ad-hoc meeting room' : 'this channel')) return; // t259u
    try { await api.removeConversationMember(conversation.id, uid); refresh(); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <Frame open={open} onClose={onClose} title={`Members (${members.length})`} {...(phone ? { testId: 'thread-members-sheet' } : {})}>
      {isChannel && limit !== undefined && (
        <p className={`mb-2 text-xs ${left === 0 ? 'font-medium text-red-700 dark:text-red-400' : 'text-slate-500'}`} data-testid="channel-capacity">
          {members.length} of {limit} people{left === 0 ? `: this ${isRoom ? 'ad-hoc meeting room' : 'channel'} is full` : ` · ${left} ${left === 1 ? 'place' : 'places'} left`}
        </p>
      )}
      {isChannel && canAdd && (
        showAdd ? (
          <div className="mb-4 space-y-2">
            <PeoplePicker selected={adding} onChange={setAdding} exclude={members.map(m => m.user_id)} max={left} />
            <div className="flex gap-2"><Button size="sm" loading={busy} onClick={() => void add()} disabled={!adding.length}>Add {adding.length || ''}</Button><Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button></div>
          </div>
        ) : (
          <div className="mb-3 flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" disabled={left === 0} onClick={() => setShowAdd(true)}><UserPlus className="h-4 w-4" />Add people</Button>
            <Button size="sm" variant="secondary" disabled={left === 0} onClick={() => setStaffList(true)} data-testid="channel-staff-list"><ListChecks className="h-4 w-4" />Add from staff list</Button>
          </div>
        )
      )}
      {conversation.kind === 'group_dm' && <p className="mb-3 text-xs text-slate-500">To add someone, start a new group message with everyone in it.</p>}
      {staffList && (
        <Modal open wide="xl" onClose={() => setStaffList(false)} title={`Add to ${where} from the staff list`}>
          <StaffListPicker inIds={members.map(m => m.user_id)} notInLabel={`Not in ${where}`} max={left} busy={busy} onAdd={ids => add(ids)} />
        </Modal>
      )}
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {members.map(m => (
          <div key={m.user_id} className="flex items-center gap-3 py-2">
            <Avatar size={30} name={m.display_name} id={m.user_id} presence={presence[m.user_id] ?? 'offline'} />
            <span className="flex-1 text-sm font-medium"><PersonButton userId={m.user_id} name={m.display_name} className="rounded hover:underline">{m.display_name}</PersonButton>{m.user_id === userId && <span className="text-slate-500"> (you)</span>}</span>
            {m.role !== 'member' && <span className="text-xs text-slate-500">{m.role}</span>}
            {isChannel && canRemove && m.user_id !== userId && (
              <Button size="icon" variant="ghost" title="Remove" aria-label={`Remove ${m.display_name}`} onClick={() => void remove(m.user_id)}><UserMinus className="h-4 w-4" /></Button>
            )}
          </div>
        ))}
      </div>
    </Frame>
  );
}

